/* eslint-disable @next/next/no-img-element */
import { useState } from 'react';

import clsx from 'clsx';

import useFormatDateTime from '@/hooks/format/useFormatDateTime';
import useTranslation from '@/utils/hooks/translation/useTranslation';

//utils
import { post } from '@/utils/client';
// Hooks
import useDarkMode from '@/hooks/utils/useDarkMode';

import PropTypes from 'prop-types';

import { FROM_COLLABORATION_NOTIFICATION } from '@/lib/scheduler/funnelContext';
//Libs
import { POST_SETTINGS } from '@/lib/scheduler/postFunnel/steps';
import { POST_STATUS, REJECTED, WAITING_FOR_APPROVAL } from '@/lib/scheduler/postStatus';
import { TYPE_STATUS } from '@/lib/scheduler/postType';

import { DefaultAvatar as Avatar } from '@/components/base/Avatar';
import Checkbox from '@/components/base/Checkbox';
// Components
import Icon from '@/components/base/Icon';
import Tooltip from '@/components/base/Tooltip';
import ContentIconBg from '@/components/icons/ContentIconBg';
import { Trans } from '@/components/tools/translation';

import { COLLABORATION_NOTIFICATIONS_MARK_AS_READ } from '@/routes/apiEndpoints';

const Img = ({ data }) => {
    const [errorImg, setErrorImage] = useState(false);

    if (errorImg || (!data?.medias?.[0]?.image && !data?.medias?.[0]?.min_thumbnail)) {
        return (
            <div className="square flex h-full w-full flex-1 items-center justify-center rounded-md bg-icoGray-300 dark:bg-icoDarkMode-deepBlack">
                {/* Change Icon to real fallback */}
                <Icon
                    icon="albums"
                    className="flex items-center justify-center text-xl text-icoGray-500 dark:text-icoDarkMode-wolf"
                />
            </div>
        );
    }

    return (
        <img
            src={data?.medias?.[0]?.image ?? data?.medias?.[0]?.min_thumbnail}
            alt=""
            className="h-full w-full rounded-md object-cover"
            onError={() => setErrorImage(true)}
        />
    );
};

Img.propTypes = {
    data: PropTypes.object
};

const Status = ({ state }) => {
    const { t } = useTranslation('scheduler');
    return (
        <div
            className="flex items-center space-x-1 rounded-m p-1 font-GMedium text-xs text-white"
            style={{ backgroundColor: POST_STATUS?.[state]?.color }}>
            <Icon icon={POST_STATUS?.[state]?.icon} />
            <p>{t(`status.${state}`)}</p>
        </div>
    );
};

Status.propTypes = {
    state: PropTypes.string
};

const CollaborationNotificationItem = ({
    notification,
    user,
    mutateNotifications,
    isChecked,
    setCheckedIds,
    openFunnel
}) => {
    const { isDarkMode } = useDarkMode();
    const isNew = !notification?.is_read;
    const postStatus = notification?.scheduler_post_status;
    const { formatDateTime } = useFormatDateTime(
        user?.settings?.timezone,
        user?.settings?.platform_language
    );

    const markAsRead = async () => {
        try {
            if (isNew) {
                post(COLLABORATION_NOTIFICATIONS_MARK_AS_READ, {
                    ids: [notification?.id]
                });
                mutateNotifications(
                    (prevState) =>
                        prevState.map((page) => ({
                            ...page,
                            data: page?.data?.map((notificationItem) => {
                                if (notificationItem?.id === notification?.id) {
                                    return {
                                        ...notificationItem,
                                        is_read: true
                                    };
                                } else {
                                    return notificationItem;
                                }
                            }),
                            summary: {
                                ...page?.summary,
                                unread_count: page?.summary?.unread_count - 1
                            }
                        })),
                    false
                );
            }
            openFunnel({
                postId: notification?.scheduler_post?.id,
                socialProfileId: notification?.social_profile?.id,
                from: FROM_COLLABORATION_NOTIFICATION,
                step: POST_SETTINGS
            });
        } catch (e) {
            // We don't manage any error state at this point because we are supposed to redirect to a scheduler post page
            console.error(e);
        }
    };

    return (
        <button onClick={markAsRead} data-testid={`notification-${notification?.id}`}>
            <div className="rounded-md bg-white p-1 dark:bg-icoDarkMode-deepBlack">
                <div
                    className={clsx('flex rounded-m px-7 py-4', {
                        'bg-icoBlue-100 dark:bg-[#043050]': isNew,
                        'bg-white dark:bg-icoDarkMode-anthracite': !isNew
                    })}>
                    {/* POST VISUAL */}
                    <div className="relative h-[4.625rem] w-[4.625rem]">
                        <Img data={notification?.scheduler_post?.data} />

                        {notification?.scheduler_post?.type !== 'image' ? (
                            <>
                                <ContentIconBg
                                    highlightColor={
                                        isNew ? (isDarkMode() ? '#043050' : '#e6f3ff') : null
                                    }
                                    className="absolute -right-0.5 -top-0.5 z-1"
                                />
                                <Icon
                                    aria-label="type-post"
                                    icon={
                                        notification?.scheduler_post?.type === TYPE_STATUS
                                            ? 'text'
                                            : notification?.scheduler_post?.type
                                    }
                                    className="absolute right-0.5 top-0.5 z-1 align-middle text-xxs text-icoGray-500"
                                />
                            </>
                        ) : null}
                    </div>
                    {/* POST INFOS */}
                    <div className="ml-6 flex flex-1 flex-col justify-between">
                        <div className="flex items-center space-x-2">
                            <Status state={notification?.scheduler_post_status} />
                            {notification?.scheduler_post_status === REJECTED ? (
                                <p className="font-GMedium text-xs">
                                    <Trans
                                        i18nKey={`scheduler:collaboration.notifications.rejected_by`}
                                        values={{
                                            author: notification?.username
                                        }}
                                        components={{ tag0: <span className="font-GSemiBold" /> }}
                                    />
                                </p>
                            ) : null}
                            {notification?.note ? (
                                <div
                                    className={clsx(
                                        'flex items-center space-x-1 rounded-m p-1 font-GMedium text-xs',
                                        {
                                            'bg-icoRed-100 text-icoRed dark:bg-icoRed/20':
                                                postStatus === REJECTED,
                                            'bg-icoPurple-100 text-icoPurple dark:bg-icoPurple/20':
                                                postStatus === WAITING_FOR_APPROVAL,
                                            'bg-icoBlue-100 text-icoBlue dark:bg-icoBlue/20':
                                                postStatus !== WAITING_FOR_APPROVAL &&
                                                postStatus !== REJECTED
                                        }
                                    )}>
                                    <Icon icon="conversations" />
                                    <span>
                                        <Trans
                                            i18nKey={`scheduler:collaboration.notifications.notes_by`}
                                            values={{
                                                number: 1,
                                                authors: notification?.note?.username
                                            }}
                                            components={{
                                                tag0: <span className="font-GSemiBold" />
                                            }}
                                        />
                                    </span>
                                </div>
                            ) : null}
                        </div>
                        <div className="mt-4 flex flex-col items-start space-y-2 lg:mt-0 lg:flex-row lg:items-center">
                            <Tooltip position="top" content={notification?.social_profile?.slug}>
                                <Avatar
                                    url={notification?.social_profile?.external_api_data?.avatar}
                                    size="sm"
                                    className="mr-4"
                                    aria-label={notification?.social_profile?.slug}
                                    fallback={notification?.social_profile?.slug?.[0]}
                                    profileType={notification?.social_profile?.social_network_type}
                                />
                            </Tooltip>

                            <div className="flex items-center space-x-2 rounded-m bg-icoGray-100 p-2 font-GMedium text-xs text-icoGray-500 dark:bg-icoDarkMode-deepBlack">
                                <Icon icon="calendar" />
                                <p>
                                    {formatDateTime(
                                        notification?.scheduler_post?.scheduled_at,
                                        `${user.settings?.date_format}_complete`
                                    )}{' '}
                                    {formatDateTime(
                                        notification?.scheduler_post?.scheduled_at,
                                        `${user.settings?.time_format}_noSeconds`
                                    )}{' '}
                                    (
                                    {formatDateTime(
                                        notification?.scheduler_post?.scheduled_at,
                                        'utc'
                                    )}
                                    )
                                </p>
                            </div>
                        </div>
                    </div>
                    {/* NOTIFICATION TIMELINE */}
                    <div className="flex items-center space-x-3">
                        <div className="hidden items-center space-x-2 font-GMedium text-xs text-icoGray-500 lg:flex">
                            <Icon icon="notification" />
                            <p>
                                {formatDateTime(
                                    notification?.created_at,
                                    `${user.settings?.time_format}_noSeconds`
                                )}{' '}
                                {formatDateTime(
                                    notification?.created_at,
                                    `${user.settings?.date_format}_complete`
                                )}
                            </p>
                        </div>
                        <Checkbox
                            id={`${notification?.id}-checkbox`}
                            checked={isChecked}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => {
                                if (isChecked) {
                                    return setCheckedIds((prevState) =>
                                        prevState.filter((id) => id !== notification?.id)
                                    );
                                }
                                setCheckedIds((prevState) => [...prevState, notification?.id]);
                            }}
                        />
                    </div>
                </div>
            </div>
        </button>
    );
};

CollaborationNotificationItem.propTypes = {
    notification: PropTypes.object,
    user: PropTypes.object,
    mutateNotifications: PropTypes.func,
    setCheckedIds: PropTypes.func
};

export default CollaborationNotificationItem;
